import React, { useState, useRef } from 'react';

interface AccordionContentProps {
  content: string;
  className?: string;
}

export function parseAccordionSections(htmlContent: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const sections: Array<{ title: string; content: string; id: string }> = [];

  const h3Elements = doc.querySelectorAll('h3');

  h3Elements.forEach((h3, index) => {
    const title = h3.textContent?.trim() || `Section ${index + 1}`;

    let sectionContent = '';
    let currentElement = h3.nextElementSibling;

    while (currentElement && currentElement.tagName !== 'H3') {
      sectionContent += currentElement.outerHTML;
      currentElement = currentElement.nextElementSibling;
    }

    if (sectionContent.trim()) {
      sections.push({
        title,
        content: sectionContent.trim(),
        id: `section-${index}`,
      });
    }
  });

  return sections;
}

const AccordionContent: React.FC<AccordionContentProps> = ({ content, className = '' }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement }>({});

  const sections = parseAccordionSections(content);

  const toggleSection = (sectionId: string) => {
    const isCurrentlyExpanded = expandedSections.has(sectionId);
    const isExpanding = !isCurrentlyExpanded;

    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });

    if (isExpanding) {
      setTimeout(() => {
        const sectionElement = sectionRefs.current[sectionId];
        if (sectionElement) {
          const elementTop = sectionElement.getBoundingClientRect().top + window.pageYOffset;
          const offset = 100;
          window.scrollTo({
            top: elementTop - offset,
            behavior: 'smooth',
          });
        }
      }, 250);
    }
  };

  if (sections.length === 0) {
    return (
      <div className={`accordion-content ${className}`}>
        <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    );
  }

  return (
    <div className={`accordion-content mx-auto max-w-4xl ${className}`}>
      <div className="space-y-4">
        {sections.map((section) => (
          <div
            key={section.id}
            ref={(el) => {
              if (el) sectionRefs.current[section.id] = el;
            }}
            className="overflow-hidden rounded-lg border border-gray-200"
          >
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full bg-gray-50 px-6 py-4 text-left transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-expanded={expandedSections.has(section.id)}
              aria-controls={`content-${section.id}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                <svg
                  className={`h-5 w-5 transform transition-transform duration-300 ${
                    expandedSections.has(section.id) ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>

            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                expandedSections.has(section.id) ? 'max-h-full opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div
                id={`content-${section.id}`}
                className="border-t border-gray-200 bg-white px-6 py-4"
              >
                <div
                  className="prose prose-lg prose-blue max-w-none"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
          .accordion-content a {
            color: #3a838e;
            text-decoration: underline;
          }

          .accordion-content a:hover {
            color: #2d6570;
          }

          .accordion-content ul {
            list-style-type: disc;
            padding-left: 1.5rem;
            margin: 1rem 0;
          }

          .accordion-content li {
            margin: 0.5rem 0;
          }

          .accordion-content p {
            margin: 1rem 0;
            line-height: 1.6;
          }

          .accordion-content strong {
            font-weight: 700;
          }
        `,
        }}
      />
    </div>
  );
};

export default AccordionContent;
